/**
 * UniversalChakraRendererManager - Event-driven manager for all universal chakra renderers
 *
 * Manages lifecycle and rendering of all chakra types:
 * - Muhurta Chakra (30 sectors)
 * - Nakshatra Chakra (27 sectors)
 * - Divisional Chakras D7-D60 (7-60 sectors)
 *
 * Architecture:
 * 1. Subscribes to chakra render events
 * 2. Creates/manages UniversalChakraRenderer instances
 * 3. Handles visibility toggling
 * 4. Cleans up on chart unload
 */

import { devWarn, devError } from '../../../../../utils/devLog';
import { EventStream } from '../core/EventStream';
import { UniversalChakraRenderer } from '../modules/UniversalChakraRenderer';
import { UniversalChakraRenderData } from '../../../../../types/UniversalChakraTypes';
import * as d3 from 'd3';

export interface ChakraRenderRequest {
    chartId: string;
    targetSelector: string;  // SVG selector where to render
    data: UniversalChakraRenderData;
    options?: {
        debugMode?: boolean;
    };
}

export interface ChakraRenderResult {
    success: boolean;
    chakraType: string;
    sectorsRendered: number;
    renderTime: number;
    chartId: string;
}

/**
 * Manager for all universal chakra renderers
 */
export class UniversalChakraRendererManager {
    private eventStream: EventStream;
    private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];

    // Map of renderers: `${chartId}_${chakraType}` => renderer instance
    private renderers: Map<string, UniversalChakraRenderer> = new Map();

    // Render status per chakra
    private renderStatus: Map<string, {
        isRendered: boolean;
        isVisible: boolean;
        renderTime: number;
        sectorCount: number;
        lastRenderHash?: string; // Hash of render parameters to detect changes
    }> = new Map();

    constructor(eventStream: EventStream) {
        this.eventStream = eventStream;
        this.setupEventSubscriptions();
    }

    /**
     * Setup event subscriptions
     */
    private setupEventSubscriptions(): void {
        // Main render request for any chakra type
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.universal.render', (event: any) => {
                this.handleRenderRequest(event);
            })
        );

        // Toggle visibility
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.universal.toggle', (event: any) => {
                this.handleToggleVisibility(event);
            })
        );

        // Show chakra
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.universal.show', (event: any) => {
                this.handleShow(event);
            })
        );

        // Hide chakra
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.universal.hide', (event: any) => {
                this.handleHide(event);
            })
        );

        // Clear specific chakra
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.universal.clear', (event: any) => {
                this.handleClear(event);
            })
        );

        // Clear all chakras for a chart
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chart.unload', (event: any) => {
                this.handleChartUnload(event);
            })
        );

        // Update label text only (optimized - no full re-render)
        this.eventSubscriptions.push(
            this.eventStream.subscribe('chakra.universal.label.update', (event: any) => {
                this.handleLabelUpdate(event);
            })
        );

    }

    /**
     * Create hash of render parameters to detect changes
     */
    private createRenderHash(data: UniversalChakraRenderData): string {
        // Create hash from key parameters that affect rendering
        const params = {
            startPointLongitude: data.startPointLongitude,
            divisions: data.divisions,
            sectorsCount: data.sectors.length,
            // Include subdivision state
            hasSubdivisions: !!(data.divisionLines && data.divisionLines.length > 0),
            // Include first and last sector degrees to detect rotation changes
            firstSectorStart: data.sectors[0]?.startDegree,
            lastSectorEnd: data.sectors[data.sectors.length - 1]?.endDegree,
            // Include position labels to detect custom label changes
            positionLabels: data.positionLabels?.map(l => `${l.name}_${l.longitude}`) || null
        };
        return JSON.stringify(params);
    }

    /**
     * Handle render request
     */
    private async handleRenderRequest(event: ChakraRenderRequest): Promise<void> {
        const startTime = performance.now();
        const { chartId, targetSelector, data, options } = event;
        const { chakraType } = data;

        try {
            // Get or create SVG layer for this chakra
            const layer = this.getOrCreateLayer(targetSelector, chakraType);
            if (!layer) {
                throw new Error(`Failed to create layer for ${chakraType} in ${targetSelector}`);
            }

            // Get or create renderer instance
            const rendererKey = `${chartId}_${chakraType}`;
            let renderer = this.renderers.get(rendererKey);

            if (!renderer) {
                renderer = new UniversalChakraRenderer(
                    chartId,
                    chakraType,
                    layer,
                    {
                        debugMode: options?.debugMode || false
                    }
                );
                this.renderers.set(rendererKey, renderer);
            }

            // OPTIMIZATION: Check if re-render is needed
            const currentHash = this.createRenderHash(data);
            const status = this.renderStatus.get(rendererKey);
            const needsRerender = !status || status.lastRenderHash !== currentHash;

            if (needsRerender) {
                // Render from data (full re-render)
                renderer.renderFromData(data);
            } else {
            }

            // Show the rendered chakra (always show after render request)
            renderer.show();

            const renderTime = performance.now() - startTime;

            // Update status
            this.renderStatus.set(rendererKey, {
                isRendered: true,
                isVisible: true,
                renderTime,
                sectorCount: data.sectors.length,
                lastRenderHash: currentHash // Save hash for future comparison
            });

            // Emit success event
            const result: ChakraRenderResult = {
                success: true,
                chakraType,
                sectorsRendered: data.sectors.length,
                renderTime,
                chartId
            };

            this.eventStream.emit({
                id: `chakra-render-completed-${chartId}-${chakraType}-${Date.now()}`,
                type: 'chakra.universal.rendered',
                source: 'universal-chakra-renderer-manager',
                timestamp: Date.now(),
                ...result
            });


        } catch (error) {
            devError(`❌ Failed to render ${chakraType} for chart ${chartId}:`, error);

            const result: ChakraRenderResult = {
                success: false,
                chakraType,
                sectorsRendered: 0,
                renderTime: performance.now() - startTime,
                chartId
            };

            this.eventStream.emit({
                id: `chakra-render-failed-${chartId}-${chakraType}-${Date.now()}`,
                type: 'chakra.universal.error',
                source: 'universal-chakra-renderer-manager',
                timestamp: Date.now(),
                ...result,
                error: error instanceof Error ? error.message : String(error)
            });
        }
    }

    /**
     * Get or create SVG layer for chakra type
     *
     * Layer order: background → chakras (by ascending divisions) → planets
     */
    private getOrCreateLayer(targetSelector: string, chakraType: string): d3.Selection<SVGGElement, unknown, HTMLElement, any> | null {
        try {
            const svg = d3.select(targetSelector);
            if (svg.empty()) {
                devError(`❌ SVG not found: ${targetSelector}`);
                return null;
            }

            // Look for existing layer
            const layerClass = `${chakraType}-chakra-layer`;
            let layer = svg.select<SVGGElement>(`.${layerClass}`);

            // Create if doesn't exist
            if (layer.empty()) {
                // Find insertion point: after background, before planets
                // Layer order should be: background-group → chakras (ascending) → everything else

                const viewportContent = svg.select('.viewport-content');
                const targetParent = viewportContent.empty() ? svg : viewportContent;
                const parentNode = targetParent.node() as SVGElement | null;

                if (!parentNode) {
                    devError(`❌ Could not find parent node for ${layerClass}`);
                    return null;
                }

                // Create the layer element
                const layerElement = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                layerElement.setAttribute('class', layerClass);
                layerElement.setAttribute('data-chakra-type', chakraType);
                layerElement.style.display = 'none';

                // Try to find background-group to insert after it
                const backgroundGroup = parentNode.querySelector('.background-group');

                if (backgroundGroup && backgroundGroup.nextSibling) {
                    // Insert after background-group (before next sibling)
                    parentNode.insertBefore(layerElement, backgroundGroup.nextSibling);
                } else if (backgroundGroup) {
                    // Background is last element, append after it
                    parentNode.appendChild(layerElement);
                } else {
                    // No background-group, insert at beginning
                    parentNode.insertBefore(layerElement, parentNode.firstChild);
                }

                // Select the newly created element with d3
                layer = d3.select(layerElement) as unknown as d3.Selection<SVGGElement, unknown, HTMLElement, any>;

            }

            return layer;

        } catch (error) {
            devError(`❌ Failed to get/create layer for ${chakraType}:`, error);
            return null;
        }
    }

    /**
     * Handle toggle visibility
     */
    private handleToggleVisibility(event: { chartId: string; chakraType: string; visible: boolean }): void {
        const { chartId, chakraType, visible } = event;
        const rendererKey = `${chartId}_${chakraType}`;
        const renderer = this.renderers.get(rendererKey);


        if (!renderer) {
            devWarn(`⚠️ No renderer found for ${chakraType} on chart ${chartId}`);
            return;
        }

        if (visible) {
            renderer.show();
        } else {
            renderer.hide();
        }

        // Update status
        const status = this.renderStatus.get(rendererKey);
        if (status) {
            status.isVisible = visible;
        }

    }

    /**
     * Handle show
     */
    private handleShow(event: { chartId: string; chakraType: string }): void {
        this.handleToggleVisibility({ ...event, visible: true });
    }

    /**
     * Handle hide
     */
    private handleHide(event: { chartId: string; chakraType: string }): void {
        this.handleToggleVisibility({ ...event, visible: false });
    }

    /**
     * Handle clear specific chakra
     */
    private handleClear(event: { chartId: string; chakraType: string }): void {
        const { chartId, chakraType } = event;
        const rendererKey = `${chartId}_${chakraType}`;
        const renderer = this.renderers.get(rendererKey);

        if (renderer) {
            renderer.destroy();
            this.renderers.delete(rendererKey);
            this.renderStatus.delete(rendererKey);
        }
    }

    /**
     * Handle chart unload - cleanup all chakras for this chart
     */
    private handleChartUnload(event: { chartId: string }): void {
        const { chartId } = event;
        let cleanedCount = 0;

        // Find and destroy all renderers for this chart
        for (const [key, renderer] of this.renderers.entries()) {
            if (key.startsWith(`${chartId}_`)) {
                renderer.destroy();
                this.renderers.delete(key);
                this.renderStatus.delete(key);
                cleanedCount++;
            }
        }

        if (cleanedCount > 0) {
        }
    }

    /**
     * Handle label update - only update label text, no full re-render (optimized)
     */
    private handleLabelUpdate(event: { chartId: string; chakraType: string; label: string }): void {
        const { chartId, chakraType, label } = event;
        const rendererKey = `${chartId}_${chakraType}`;
        const renderer = this.renderers.get(rendererKey);

        if (renderer) {
            renderer.updatePositionLabelText(label);
        }
    }

    /**
     * Get render status
     */
    public getStatus(chartId: string, chakraType: string) {
        return this.renderStatus.get(`${chartId}_${chakraType}`);
    }

    /**
     * Get all renderers for debugging
     */
    public getRenderers(): Map<string, UniversalChakraRenderer> {
        return this.renderers;
    }

    /**
     * Cleanup all renderers
     */
    public destroy(): void {
        // Unsubscribe from all event subscriptions
        this.eventSubscriptions.forEach(sub => sub.unsubscribe());
        this.eventSubscriptions = [];

        for (const renderer of this.renderers.values()) {
            renderer.destroy();
        }
        this.renderers.clear();
        this.renderStatus.clear();
    }
}
